import { EPN_CONFIG } from '../config/epn';

/**
 * Wraps eBay URLs with EPN tracking parameters
 * @param ebayUrl - The original eBay URL
 * @returns The tracking URL with your Campaign ID and custom ID
 */
export function wrapEbayUrl(ebayUrl: string): string {
  // If tracking is disabled, return original URL
  if (!EPN_CONFIG.ENABLE_TRACKING) {
    console.log('[EPN] Tracking disabled, using original URL');
    return ebayUrl;
  }

  // If no URL provided or invalid, return original
  if (!ebayUrl || ebayUrl === '#' || ebayUrl === '') {
    return ebayUrl;
  }

  // If already a tracking URL, return as is
  if (ebayUrl.includes('rover.ebay.com')) {
    return ebayUrl;
  }

  try {
    // Validate the eBay URL first
    const url = new URL(ebayUrl);
    if (!url.hostname.includes('ebay.com')) {
      console.warn('[EPN] Not an eBay URL, returning original:', ebayUrl);
      return ebayUrl;
    }

    // For now, let's use a simpler approach to test
    // Add tracking parameters directly to the eBay URL
    const separator = ebayUrl.includes('?') ? '&' : '?';
    const trackingUrl = `${ebayUrl}${separator}campid=${EPN_CONFIG.CAMPAIGN_ID}&toolid=${EPN_CONFIG.TOOL_ID}&customid=${EPN_CONFIG.CUSTOM_ID}`;
    
    console.log('[EPN] Generated tracking URL:', trackingUrl);
    return trackingUrl;
  } catch (error) {
    console.error('[EPN] Failed to generate tracking URL:', error);
    return ebayUrl; // Fallback to original URL
  }
}

/**
 * Checks if a URL is an eBay URL
 * @param url - The URL to check
 * @returns True if it's an eBay URL
 */
export function isEbayUrl(url: string): boolean {
  if (!url) return false;
  
  const ebayDomains = [
    'ebay.com',
    'ebay.co.uk',
    'ebay.ca',
    'ebay.com.au',
    'ebay.de',
    'ebay.fr',
    'ebay.it',
    'ebay.es'
  ];
  
  try {
    const urlObj = new URL(url);
    return ebayDomains.some(domain => urlObj.hostname.includes(domain));
  } catch {
    return false;
  }
}

/**
 * Opens an eBay URL with tracking in a new tab
 * @param ebayUrl - The eBay URL to open
 */
export function openEbayUrl(ebayUrl: string): void {
  const trackingUrl = wrapEbayUrl(ebayUrl);
  window.open(trackingUrl, '_blank', 'noopener,noreferrer');
} 